import { DateTime } from 'luxon';

import { ZonedDateTimeRange } from '@rygen/rygen-ui/model';
import { loadLuxonDateTime } from '@rygen/rygen-ui/util';

import { ShipmentStopType } from '../shipment/ShipmentStopType';

import { ShipmentEventEquipment } from './ShipmentEventEquipment';
import { ShipmentEventLocation } from './ShipmentEventLocation';
import { ShipmentEventRelatedStopReference } from './ShipmentEventRelatedStopReference';
import { ShipmentEventSeverity } from './ShipmentEventSeverity';
import { ShipmentEventSource } from './ShipmentEventSource';
import {
  isMilestone,
  isShipmentMilestoneAssociatedEventType,
  ShipmentEventType,
} from './ShipmentEventType';

export class ShipmentEvent {
  id?: number;
  shipmentId?: number;
  // todo mark "@deprecated Use {@link relatedStop} instead"
  //   and convert all business logic to use that as well
  //   https://rygen.atlassian.net/browse/CS-5252
  relatedStopId?: number;
  relatedStop?: ShipmentEventRelatedStopReference;
  stopType?: ShipmentStopType;
  eventType?: ShipmentEventType;
  severity?: ShipmentEventSeverity;
  expectedDateRange?: ZonedDateTimeRange;
  actualLocation: ShipmentEventLocation = new ShipmentEventLocation();
  actualDate?: DateTime;
  equipments: ShipmentEventEquipment[] = [];
  comment?: string;
  carrierId?: number;
  source?: ShipmentEventSource;
  late: boolean = false;

  constructor(opts?: Partial<ShipmentEvent>) {
    if (opts?.id != null) {
      this.id = opts.id;
    }
    if (opts?.shipmentId != null) {
      this.shipmentId = opts.shipmentId;
    }
    if (opts?.relatedStopId != null) {
      this.relatedStopId = opts.relatedStopId;
    }
    if (opts?.relatedStop != null) {
      this.relatedStop = new ShipmentEventRelatedStopReference(
        opts.relatedStop,
      );
      this.relatedStopId = opts.relatedStop.id;
    }
    if (opts?.stopType != null) {
      if (typeof opts?.stopType === 'string') {
        this.stopType = ShipmentStopType[opts.stopType];
      } else {
        this.stopType = opts.stopType;
      }
    }
    if (typeof opts?.eventType === 'string') {
      this.eventType = ShipmentEventType[opts.eventType];
    } else if (opts?.eventType != null) {
      this.eventType = opts.eventType;
    }
    if (typeof opts?.severity === 'string') {
      this.severity = ShipmentEventSeverity[opts.severity];
    } else if (opts?.severity != null) {
      this.severity = opts.severity;
    }
    if (opts?.expectedDateRange != null) {
      this.expectedDateRange = new ZonedDateTimeRange(
        opts.expectedDateRange,
        opts?.actualLocation?.timeZone,
      );
    }
    if (opts?.actualLocation != null) {
      this.actualLocation = new ShipmentEventLocation(opts.actualLocation);
    }
    this.actualDate = loadLuxonDateTime(
      opts?.actualDate,
      opts?.actualLocation?.timeZone,
    );
    if (opts?.equipments != null) {
      this.equipments = opts.equipments.map(
        (val) => new ShipmentEventEquipment(val),
      );
    }
    if (opts?.comment != null) {
      this.comment = opts.comment;
    }
    if (opts?.carrierId != null) {
      this.carrierId = opts.carrierId;
    }
    if (opts?.source != null) {
      this.source = opts.source;
    }
    if (typeof opts?.source === 'string') {
      this.source = ShipmentEventSource[opts.source];
    } else if (opts?.source != null) {
      this.source = opts.source;
    }
    if (opts?.late != null) {
      this.late = opts.late;
    }
  }

  get isMilestone(): boolean {
    if (!this.eventType) {
      return false;
    }

    const isMilestoneEvent = isMilestone(this.eventType);
    if (isMilestoneEvent) {
      console.warn(
        '`ShipmentEvent.isMilestone` returned true. This event should be a `ShipmentMilestoneEvent.',
      );
    }

    return isMilestoneEvent;
  }

  get isShipmentMilestoneAssociatedEvent(): boolean {
    return isShipmentMilestoneAssociatedEventType(this.eventType);
  }

  isResolved(_requireBothDate: boolean = false) {
    return !!this.actualDate;
  }
}
